import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { Button, Input, Textarea } from '../../../ui';
import { getProgramsByIds } from '../../../../content/meeting-programs';
import type { SobrietyDateFormData } from '../../../../types';
import { PlusIcon, TrashIcon, CalendarIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useOnboarding } from '../../../../stores/onboardingStore';

const sobrietyDateSchema = z.object({
  label: z.string().min(1, 'Please give this sobriety date a name'),
  startDate: z.string().min(1, 'Please select a date'),
  definition: z.string().optional(),
  programId: z.string().min(1, 'Please select a program'),
});

type SobrietyDateSchemaType = z.infer<typeof sobrietyDateSchema>;

interface SobrietyStepProps {
  selectedPrograms: string[];
  sobrietyDates: SobrietyDateFormData[];
  onSubmit: (dates: SobrietyDateFormData[]) => void;
}

function SobrietyDateCard({
  date,
  onRemove,
  index,
}: {
  date: SobrietyDateFormData;
  onRemove: () => void;
  index: number;
}) {
  const daysSober = Math.floor(
    (new Date().getTime() - date.startDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -10 }}
      transition={{ delay: index * 0.05 }}
      className="relative bg-white dark:bg-surface-800 rounded-xl p-4 border border-surface-200 dark:border-surface-700 shadow-sm"
    >
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-3 right-3 p-1.5 rounded-lg text-surface-400 hover:text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
        aria-label="Remove sobriety date"
      >
        <TrashIcon className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 p-2 rounded-lg bg-accent-100 dark:bg-accent-900/30">
          <CalendarIcon className="w-5 h-5 text-accent-600 dark:text-accent-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-surface-800 dark:text-white">{date.label}</h4>
          <p className="text-sm text-surface-500 dark:text-surface-400 mt-0.5">
            Since {format(date.startDate, 'MMMM d, yyyy')}
          </p>
          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-50 dark:bg-accent-900/20">
            <span className="text-lg font-bold text-accent-700 dark:text-accent-300">
              {daysSober.toLocaleString()}
            </span>
            <span className="text-xs text-accent-600 dark:text-accent-400">
              {daysSober === 1 ? 'day' : 'days'}
            </span>
          </div>
          {date.definition && (
            <p className="mt-2 text-xs text-surface-500 dark:text-surface-400 italic">
              "{date.definition}"
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export function SobrietyStep({ selectedPrograms, sobrietyDates, onSubmit }: SobrietyStepProps) {
  const [dates, setDates] = useState<SobrietyDateFormData[]>(sobrietyDates);
  const [isAddingNew, setIsAddingNew] = useState(dates.length === 0);
  const { data } = useOnboarding();

  // Merge official programs with custom programs
  const officialPrograms = getProgramsByIds(selectedPrograms);
  const customProgramsFormatted = data.customPrograms.map((cp) => ({
    id: cp.id,
    name: cp.name,
    shortName: cp.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 3),
    description: cp.description || '',
    color: '#8b7355', // neutral brown for custom programs
    category: 'custom' as const,
  }));
  const programs = [...officialPrograms, ...customProgramsFormatted];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SobrietyDateSchemaType>({
    resolver: zodResolver(sobrietyDateSchema),
    defaultValues: {
      programId: programs[0]?.id || '',
    },
  });

  const onAddDate = (data: SobrietyDateSchemaType) => {
    const newDate: SobrietyDateFormData = {
      label: data.label,
      startDate: new Date(data.startDate),
      definition: data.definition || '',
      programId: data.programId,
    };
    setDates((prev) => [...prev, newDate]);
    reset();
    setIsAddingNew(false);
  };

  const removeDate = (index: number) => {
    setDates((prev) => prev.filter((_, i) => i !== index));
  };

  const handleContinue = () => {
    onSubmit(dates);
  };

  return (
    <div>
      {/* Intro text */}
      <motion.div
        className="text-center mb-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        <p className="text-surface-600 dark:text-surface-400">
          Track your sobriety journey. Add a date for each area of recovery you're working on.
          This step is optional.
        </p>
      </motion.div>

      {/* Existing sobriety dates */}
      <AnimatePresence>
        {dates.length > 0 && (
          <motion.div
            className="space-y-3 mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {dates.map((date, index) => (
              <SobrietyDateCard
                key={`${date.label}-${date.startDate.toISOString()}`}
                date={date}
                onRemove={() => removeDate(index)}
                index={index}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add new form */}
      <AnimatePresence mode="wait">
        {isAddingNew ? (
          <motion.form
            key="add-form"
            onSubmit={handleSubmit(onAddDate)}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-surface-50 dark:bg-surface-800/50 rounded-xl p-5 border border-surface-200 dark:border-surface-700 space-y-4"
          >
            <h3 className="font-semibold text-surface-800 dark:text-white">
              {dates.length === 0 ? 'Add Your First Sobriety Date' : 'Add Another Sobriety Date'}
            </h3>

            <Input
              label="What are you tracking?"
              placeholder="e.g., Alcohol, Pornography, Gambling"
              {...register('label')}
              error={errors.label?.message}
            />

            <Input
              label="Sobriety Date"
              type="date"
              max={format(new Date(), 'yyyy-MM-dd')}
              {...register('startDate')}
              error={errors.startDate?.message}
            />

            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
                Associated Program
              </label>
              <div className="relative">
                <select
                  {...register('programId')}
                  className="w-full px-4 py-2.5 pr-10 text-base rounded-xl border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent appearance-none cursor-pointer"
                  style={{ fontSize: '16px' }}
                >
                  {programs.map((program) => (
                    <option key={program.id} value={program.id} style={{ fontSize: '16px' }}>
                      {program.shortName} - {program.name}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-surface-400">
                  <ChevronDownIcon className="w-5 h-5" />
                </div>
              </div>
              {errors.programId && (
                <p className="mt-1 text-sm text-danger-500">{errors.programId.message}</p>
              )}
            </div>

            <Textarea
              label="Sobriety Definition (optional)"
              placeholder="What does maintaining sobriety mean to you? e.g., No alcohol of any kind, even in cooking"
              rows={2}
              {...register('definition')}
              hint="This helps you stay accountable to your own standards"
            />

            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1">
                Add Date
              </Button>
              {dates.length > 0 && (
                <Button type="button" variant="ghost" onClick={() => setIsAddingNew(false)}>
                  Cancel
                </Button>
              )}
            </div>
          </motion.form>
        ) : (
          <motion.button
            key="add-button"
            type="button"
            onClick={() => setIsAddingNew(true)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="w-full p-4 rounded-xl border-2 border-dashed border-surface-300 dark:border-surface-600 text-surface-500 dark:text-surface-400 hover:border-accent-400 hover:text-accent-600 dark:hover:border-accent-500 dark:hover:text-accent-400 transition-colors flex items-center justify-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            Add {dates.length > 0 ? 'Another' : 'a'} Sobriety Date
          </motion.button>
        )}
      </AnimatePresence>

      {/* Continue button */}
      <motion.div
        className="pt-6"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Button onClick={handleContinue} size="lg" className="w-full">
          {dates.length === 0 ? 'Skip for Now' : 'Continue'}
        </Button>
        {dates.length === 0 && (
          <p className="text-center text-xs text-surface-500 dark:text-surface-400 mt-2">
            You can add sobriety dates later in settings
          </p>
        )}
      </motion.div>
    </div>
  );
}
